import { useState, useEffect } from 'react'
import { TenantGuard } from '@/components/TenantGuard'
import { useAuth } from '@/hooks/useApi'
import { useTenantContext } from '@/contexts/TenantContext'
import { apiClient } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { DataTable } from '@/components/ui/data-table'
import { Badge } from '@/components/ui/badge'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Checkbox } from '@/components/ui/checkbox'
import { Plus, Settings, Archive, Trash2, Eye, EyeOff, Search, QrCode, ExternalLink, Upload, X, Download } from 'lucide-react'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'

interface Queue {
  location_id: string
  service_id: string
  waiting: number
  called: number
  being_serviced: number
  no_show: number
  no_show_delayed: number
  max_strikes: number
  left: number
  archived: boolean
  allow_multi_called: boolean
}

interface QueueConfig {
  tenant_id: string
  location_id: string
  service_id: string
  image_url: string
  single_active_phone: boolean
  no_show_delay_minutes: number
  no_show_timeout_minutes: number
  no_show_requeue_position: string
  max_strikes: number
  estimated_service_time_minutes: number
  announcement: string
  party_size_enabled: boolean
  max_party_size: number
  change_party_size_mode?: string
  join_keyword?: string
  questions: Array<{
    key: string
    label: string
    type: string
    required: boolean
    options?: string[]
  }>
  updated_at?: number
}

interface QueuesProps {
  locations?: any[]
  services?: any[]
  onRefresh?: () => void
}

export function Queues({ locations: propLocations, services: propServices }: QueuesProps = {}) {
  const { isSuperAdmin, user } = useAuth();
  const { selectedTenantId } = useTenantContext();
  const activeTenantId = selectedTenantId || user?.tenant_from_group;
  const [queues, setQueues] = useState<Queue[]>([])
  const [locations, setLocations] = useState<any[]>(propLocations || [])
  const [services, setServices] = useState<any[]>(propServices || [])
  const [loading, setLoading] = useState(true)
  const [showArchived, setShowArchived] = useState(false)
  
  // Fetch functions for locations and services (only used if not provided as props)
  const fetchLocations = async () => {
    if (!activeTenantId || propLocations) return
    try {
      const response = await apiClient.getLocations(activeTenantId)
      setLocations(response.locations || [])
    } catch (err) {
      console.error('Failed to fetch locations:', err)
    }
  }
  
  const fetchServices = async () => {
    if (!activeTenantId || propServices) return
    try {
      const response = await apiClient.getServices(activeTenantId)
      setServices(response.services || [])
    } catch (err) {
      console.error('Failed to fetch services:', err)
    }
  }
  const [searchTerm, setSearchTerm] = useState('')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showConfigModal, setShowConfigModal] = useState(false)
  const [selectedQueue, setSelectedQueue] = useState<Queue | null>(null)
  const [, setQueueConfig] = useState<QueueConfig | null>(null)
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  const [showQrModal, setShowQrModal] = useState(false)
  const [qrUrl, setQrUrl] = useState('')
  const [uploading, setUploading] = useState(false)
  const [imagePreview, setImagePreview] = useState('')
  const [maxQueues, setMaxQueues] = useState(2) // Default to Professional plan limit

  // Form state for queue creation/editing
  const [formData, setFormData] = useState({
    location_id: '',
    service_id: '',
    image_url: '',
    single_active_phone: false,
    no_show_delay_minutes: 30,
    no_show_timeout_minutes: 30,
    no_show_requeue_position: 'end',
    max_strikes: 3,
    estimated_service_time_minutes: 10,
    announcement: '',
    party_size_enabled: false,
    max_party_size: 10,
    change_party_size_mode: 'disabled',
    join_keyword: '',
    questions: [] as Array<{
      key: string
      label: string
      type: string
      required: boolean
      options?: string[]
    }>
  })

  const fetchQueues = async () => {
    if (!activeTenantId) return
    
    try {
      setLoading(true)
      const response = await apiClient.getQueuesSummary(activeTenantId)
      setQueues((response.queues || []).map((q: any) => ({
        ...q,
        no_show_delayed: q.no_show_delayed || 0,
        max_strikes: q.max_strikes || 0,
        left: q.left || 0,
        allow_multi_called: q.allow_multi_called || false
      })))
    } catch (err) {
      console.error('Failed to fetch queues:', err)
      setQueues([])
    } finally {
      setLoading(false)
    }
  }

  const fetchQueueConfig = async (locationId: string, serviceId: string) => {
    if (!activeTenantId) return null
    
    try {
      const response = await apiClient.getAdminQueueConfig(activeTenantId, locationId, serviceId)
      return response
    } catch (err) {
      console.error('Failed to fetch queue config:', err)
      return null
    }
  }

  const fetchPlanLimits = async () => {
    if (!activeTenantId) return
    try {
      const response = await apiClient.getSubscription(activeTenantId)
      const plan = response.current_plan || 'trial'
      
      // Map plan to queue limits (Professional/Trial: 2, Premium: 10, Enterprise: 20)
      if (plan.includes('trial') || plan.includes('professional')) {
        setMaxQueues(2)
      } else if (plan.includes('premium')) {
        setMaxQueues(10)
      } else if (plan.includes('enterprise')) {
        setMaxQueues(20)
      } else {
        setMaxQueues(2) // Default to Professional
      }
    } catch (err) {
      console.error('Failed to fetch plan limits:', err)
      setMaxQueues(2) // Default to Professional on error
    }
  }

  useEffect(() => {
    fetchQueues()
    fetchPlanLimits()
    if (!propLocations) fetchLocations()
    if (!propServices) fetchServices()
  }, [activeTenantId])
  
  // Update local state when props change
  useEffect(() => {
    if (propLocations) setLocations(propLocations)
  }, [propLocations])
  
  useEffect(() => {
    if (propServices) setServices(propServices)
  }, [propServices])

  // Listen for tenant changes and refresh data
  useEffect(() => {
    const handleTenantChange = () => {
      fetchQueues()
    }
    
    window.addEventListener('tenant-changed', handleTenantChange)
    
    return () => {
      window.removeEventListener('tenant-changed', handleTenantChange)
    }
  }, [])

  const filteredQueues = queues.filter(queue => {
    const matchesSearch = !searchTerm || 
      queue.location_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      queue.service_id.toLowerCase().includes(searchTerm.toLowerCase())
    
    const matchesArchived = showArchived || !queue.archived
    
    return matchesSearch && matchesArchived
  })

  const handleCreateQueue = () => {
    // Check queue limits
    const activeQueues = queues.filter(q => !q.archived).length
    
    if (activeQueues >= maxQueues) {
      const planName = maxQueues <= 2 ? 'Professional' : (maxQueues <= 10 ? 'Premium' : 'Enterprise')
      alert(`${planName} plan allows ${maxQueues} active queues. You currently have ${activeQueues}. Please archive an existing queue or upgrade your plan.`)
      return
    }
    
    setFormData({
      location_id: '',
      service_id: '',
      image_url: '',
      single_active_phone: false,
      no_show_delay_minutes: 30,
      no_show_timeout_minutes: 30,
      no_show_requeue_position: 'end',
      max_strikes: 3,
      estimated_service_time_minutes: 10,
      announcement: '',
      party_size_enabled: false,
      max_party_size: 10,
      change_party_size_mode: 'disabled',
      join_keyword: '',
      questions: []
    })
    setSelectedQueue(null)
    setShowCreateModal(true)
    setError('')
  }

  const handleEditQueue = async (queue: Queue) => {
    setSelectedQueue(queue)
    const config = await fetchQueueConfig(queue.location_id, queue.service_id)
    
    if (config) {
      setQueueConfig(config)
      setFormData({
        location_id: config.location_id,
        service_id: config.service_id,
        image_url: config.image_url || '',
        single_active_phone: config.single_active_phone || false,
        no_show_delay_minutes: config.no_show_delay_minutes || 30,
        no_show_timeout_minutes: config.no_show_timeout_minutes || 30,
        no_show_requeue_position: config.no_show_requeue_position || 'end',
        max_strikes: config.max_strikes || 3,
        estimated_service_time_minutes: config.estimated_service_time_minutes || 10,
        announcement: config.announcement || '',
        party_size_enabled: config.party_size_enabled || false,
        max_party_size: config.max_party_size || 10,
        change_party_size_mode: config.change_party_size_mode || 'disabled',
        join_keyword: config.join_keyword || '',
        questions: config.questions || []
      })
    }
    
    setShowConfigModal(true)
    setError('')
  }

  // Sanitize input for backend compatibility
  const sanitizeId = (input: string): string => {
    return input
      .trim()
      .replace(/\s+/g, '_')  // Replace spaces with underscores
      .replace(/[^a-zA-Z0-9_-]/g, '')  // Remove invalid characters
      .substring(0, 50)  // Limit length
  }

  const handleSaveQueue = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!activeTenantId) return
    
    const sanitizedLocationId = sanitizeId(formData.location_id)
    const sanitizedServiceId = sanitizeId(formData.service_id)
    
    if (!sanitizedLocationId || !sanitizedServiceId) {
      setError('Location ID and Service ID are required and must contain valid characters')
      return
    }
    
    setSaving(true)
    setError('')
    
    try {
      await apiClient.saveQueueConfig(activeTenantId, {
        location_id: sanitizedLocationId,
        service_id: sanitizedServiceId,
        image_url: formData.image_url,
        single_active_phone: formData.single_active_phone,
        no_show_delay_minutes: formData.no_show_delay_minutes,
        no_show_timeout_minutes: formData.no_show_timeout_minutes,
        no_show_requeue_position: formData.no_show_requeue_position,
        max_strikes: formData.max_strikes,
        estimated_service_time_minutes: formData.estimated_service_time_minutes,
        announcement: formData.announcement,
        party_size_enabled: formData.party_size_enabled,
        max_party_size: formData.max_party_size,
        change_party_size_mode: formData.change_party_size_mode,
        join_keyword: formData.join_keyword?.trim().toUpperCase() || '',
        questions: formData.questions.map(q => ({
          ...q,
          options: q.options?.filter((opt: string) => opt.trim()) || []
        }))
      })
      
      setShowCreateModal(false)
      setShowConfigModal(false)
      fetchQueues()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save queue')
    } finally {
      setSaving(false)
    }
  }

  const handleArchiveQueue = async (queue: Queue) => {
    if (!activeTenantId) return
    
    const action = queue.archived ? 'unarchive' : 'archive'
    if (!confirm(`${action} queue ${queue.location_id}/${queue.service_id}?`)) return
    
    try {
      await apiClient.archiveQueue(activeTenantId, queue.location_id, queue.service_id, !queue.archived)
      fetchQueues()
    } catch (err) {
      alert(`Failed to ${action} queue`)
    }
  }

  const handleDeleteQueue = async (queue: Queue) => {
    if (!activeTenantId) return
    
    if (!confirm(`Delete queue ${queue.location_id}/${queue.service_id}? This will delete all queue entries and cannot be undone.`)) return
    
    try {
      await apiClient.deleteQueue(activeTenantId, queue.location_id, queue.service_id)
      fetchQueues()
    } catch (err) {
      alert('Failed to delete queue')
    }
  }

  const addQuestion = () => {
    setFormData(prev => ({
      ...prev,
      questions: [...prev.questions, {
        key: `q${Date.now()}`, // Use timestamp for unique keys
        label: '',
        type: 'text',
        required: false
      }]
    }))
  }

  const removeQuestion = (index: number) => {
    setFormData(prev => ({
      ...prev,
      questions: prev.questions.filter((_, i) => i !== index)
    }))
  }

  const updateQuestion = (index: number, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      questions: prev.questions.map((q, i) => 
        i === index ? { ...q, [field]: value } : q
      )
    }))
  }

  const handleGenerateQR = (queue: Queue) => {
    // Use current hostname to support dev/test/prod environments
    const hostname = window.location.hostname
    const url = `https://${hostname}/join/${activeTenantId}/${queue.location_id}/${queue.service_id}`
    setQrUrl(url)
    setSelectedQueue(queue)
    setShowQrModal(true)
  }

  const handleGenerateURL = (queue: Queue) => {
    // Use current hostname to support dev/test/prod environments
    const hostname = window.location.hostname
    const url = `https://${hostname}/join/${activeTenantId}/${queue.location_id}/${queue.service_id}`
    window.open(url, '_blank')
  }

  const copyToClipboard = () => {
    navigator.clipboard.writeText(qrUrl)
    alert('URL copied to clipboard!')
  }

  const downloadQRCode = () => {
    const qrImageUrl = `https://api.qrserver.com/v1/create-qr-code/?size=400x400&data=${encodeURIComponent(qrUrl)}`
    const link = document.createElement('a')
    link.href = qrImageUrl
    link.download = `qr-code-${selectedQueue?.location_id}-${selectedQueue?.service_id}.png`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const handleImageUpload = async (file: File) => {
    if (!file || !activeTenantId) return

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select a valid image file')
      return
    }

    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image file must be smaller than 5MB')
      return
    }

    setUploading(true)
    setError('')

    try {
      // Create preview
      const reader = new FileReader()
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string)
      }
      reader.readAsDataURL(file)

      // Upload to server
      const uploadFormData = new FormData()
      uploadFormData.append('file', file)

      const result = await apiClient.uploadImage(uploadFormData, {
        tenant: activeTenantId,
        location: formData.location_id || 'default',
        service: formData.service_id || 'default'
      })
      
      // Update form data with the uploaded image URL
      setFormData(prev => ({ ...prev, image_url: result.url }))
      setImagePreview('')  // Clear preview since we now have the real URL
      
    } catch (err) {
      console.error('Upload error:', err)
      setError('Failed to upload image. Please try again.')
      setImagePreview('')  // Clear preview on error
    } finally {
      setUploading(false)
    }
  }

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      handleImageUpload(file)
    }
    // Reset the file input
    e.target.value = ''
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    
    const files = Array.from(e.dataTransfer.files)
    const imageFile = files.find(file => file.type.startsWith('image/'))
    
    if (imageFile) {
      handleImageUpload(imageFile)
    } else {
      setError('Please drop a valid image file')
    }
  }

  const queueColumns = [
    {
      key: 'location_id' as const,
      header: 'Queue',
      render: (_: any, row: Queue) => (
        <div>
          <div className="font-medium">{row.location_id}</div>
          <div className="text-sm text-gray-500">{row.service_id}</div>
        </div>
      )
    },
    {
      key: 'waiting' as const,
      header: 'Status',
      render: (_: any, row: Queue) => (
        <div className="flex gap-1">
          {row.archived && <Badge variant="secondary">Archived</Badge>}
          {row.waiting > 0 && <Badge variant="default">{row.waiting} waiting</Badge>}
          {row.called > 0 && <Badge variant="destructive">{row.called} called</Badge>}
          {row.being_serviced > 0 && <Badge variant="default">{row.being_serviced} serving</Badge>}
        </div>
      )
    },
    {
      key: 'called' as const,
      header: 'Activity',
      render: (_: any, row: Queue) => (
        <div className="text-sm">
          <div>Active: {row.waiting + row.called + row.being_serviced}</div>
          <div className="text-gray-500">
            No-shows: {row.no_show} | Left: {row.left}
          </div>
        </div>
      )
    },
    {
      key: 'archived' as const,
      header: 'Actions',
      render: (_: any, row: Queue) => (
        <div className="flex gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleEditQueue(row)}
            title="Configure Queue"
          >
            <Settings className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleArchiveQueue(row)}
            title={row.archived ? "Unarchive Queue" : "Archive Queue"}
          >
            <Archive className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleGenerateQR(row)}
            title="Generate QR Code"
          >
            <QrCode className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleGenerateURL(row)}
            title="Open Queue URL"
          >
            <ExternalLink className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleDeleteQueue(row)}
            title="Delete Queue"
            className="text-red-600 hover:text-red-700"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      )
    }
  ]

  const queuesContent = (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Queue Management</h1>
        <p className="text-gray-600">
          Manage queues {isSuperAdmin && <Badge variant="outline" className="ml-2">Super Admin</Badge>}
        </p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle>Queues</CardTitle>
              <CardDescription>Configure and monitor your queues</CardDescription>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setShowArchived(!showArchived)}
              >
                {showArchived ? <EyeOff className="h-4 w-4 mr-2" /> : <Eye className="h-4 w-4 mr-2" />}
                {showArchived ? 'Hide Archived' : 'Show Archived'}
              </Button>
              <Button onClick={handleCreateQueue}>
                <Plus className="h-4 w-4 mr-2" />
                Create Queue
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder="Search queues..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {loading ? (
            <div>Loading queues...</div>
          ) : (
            <DataTable
              data={filteredQueues}
              columns={queueColumns}
              searchable={false}
            />
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Queue Modal */}
      <Dialog open={showCreateModal || showConfigModal} onOpenChange={(open) => {
        if (!open) {
          setShowCreateModal(false)
          setShowConfigModal(false)
        }
      }}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedQueue ? 'Configure Queue' : 'Create New Queue'}
            </DialogTitle>
            <DialogDescription>
              {selectedQueue 
                ? `Configure settings for ${selectedQueue.location_id}/${selectedQueue.service_id}`
                : 'Create a new queue with custom settings'
              }
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSaveQueue} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="locationId">Location *</Label>
                <Select
                  value={formData.location_id}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, location_id: value }))}
                  disabled={!!selectedQueue}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a location" />
                  </SelectTrigger>
                  <SelectContent>
                    {locations.map((loc) => (
                      <SelectItem key={loc.location_id} value={loc.location_id}>
                        {loc.name} ({loc.location_id})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {locations.length === 0 && (
                  <div className="text-xs text-amber-600">
                    No locations available. Create locations in the Locations tab first.
                  </div>
                )}
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="serviceId">Service *</Label>
                <Select
                  value={formData.service_id}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, service_id: value }))}
                  disabled={!!selectedQueue}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                  <SelectContent>
                    {services.map((svc) => (
                      <SelectItem key={svc.service_id} value={svc.service_id}>
                        {svc.name} ({svc.service_id})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {services.length === 0 && (
                  <div className="text-xs text-amber-600">
                    No services available. Create services in the Services tab first.
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="imageUrl">Queue Image</Label>
              <div className="space-y-2">
                {(formData.image_url || imagePreview) && (
                  <div className="relative inline-block">
                    <img 
                      src={imagePreview || formData.image_url} 
                      alt="Queue preview" 
                      className="w-32 h-32 object-cover rounded border"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-red-500 text-white hover:bg-red-600"
                      onClick={() => {
                        setFormData(prev => ({ ...prev, image_url: '' }))
                        setImagePreview('')
                      }}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                )}
                <div className="flex gap-2">
                  <Input
                    id="imageUrl"
                    value={formData.image_url}
                    onChange={(e) => setFormData(prev => ({ ...prev, image_url: e.target.value }))}
                    placeholder="Enter image URL or drag & drop image here"
                    className="flex-1"
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                  />
                  <div className="relative">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileInputChange}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                      disabled={uploading}
                      id="file-upload"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      disabled={uploading}
                      className="relative pointer-events-none"
                      asChild
                    >
                      <label htmlFor="file-upload" className="cursor-pointer pointer-events-auto flex items-center gap-2">
                        {uploading ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900"></div>
                        ) : (
                          <Upload className="h-4 w-4" />
                        )}
                        {uploading ? 'Uploading...' : 'Upload'}
                      </label>
                    </Button>
                  </div>
                </div>
                <div className="text-xs text-gray-500">
                  Upload an image, enter a URL, or drag & drop an image. Recommended size: 400x300px, max 5MB
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="announcement">Announcement</Label>
              <ReactQuill
                value={formData.announcement}
                onChange={(value) => setFormData(prev => ({ ...prev, announcement: value }))}
                placeholder="Optional announcement text with formatting"
                modules={{
                  toolbar: [
                    ['bold', 'italic', 'underline'],
                    [{ 'size': ['small', false, 'large', 'huge'] }],
                    [{ 'color': [] }, { 'background': [] }],
                    ['link'],
                    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                    ['clean']
                  ]
                }}
                className="bg-white"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="estimatedServiceTime">Estimated Service Time (minutes)</Label>
                <Input
                  id="estimatedServiceTime"
                  type="number"
                  min="1"
                  max="120"
                  value={formData.estimated_service_time_minutes}
                  onChange={(e) => setFormData(prev => ({ ...prev, estimated_service_time_minutes: parseInt(e.target.value) || 10 }))}
                />
                <div className="text-xs text-gray-500">Used for wait time predictions when insufficient historical data</div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="noShowTimeout">No-Show Timeout (minutes)</Label>
                <Input
                  id="noShowTimeout"
                  type="number"
                  min="5"
                  max="180"
                  value={formData.no_show_timeout_minutes}
                  onChange={(e) => setFormData(prev => ({ ...prev, no_show_timeout_minutes: parseInt(e.target.value) || 30 }))}
                />
                <div className="text-xs text-gray-500">Time before marking as no-show</div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="noShowDelay">No-Show Requeue Delay (minutes)</Label>
                <Input
                  id="noShowDelay"
                  type="number"
                  min="5"
                  max="180"
                  value={formData.no_show_delay_minutes}
                  onChange={(e) => setFormData(prev => ({ ...prev, no_show_delay_minutes: parseInt(e.target.value) || 30 }))}
                />
                <div className="text-xs text-gray-500">Delay before automatic requeue</div>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="noShowRequeue">No-Show Requeue Position</Label>
              <Select
                value={formData.no_show_requeue_position}
                onValueChange={(value) => setFormData(prev => ({ ...prev, no_show_requeue_position: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="end">End of queue</SelectItem>
                  <SelectItem value="front">Front of queue</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="joinKeyword">SMS Join Keyword (Optional)</Label>
              <Input
                id="joinKeyword"
                value={formData.join_keyword}
                onChange={(e) => setFormData(prev => ({ ...prev, join_keyword: e.target.value.toUpperCase() }))}
                placeholder="e.g., JOIN, CHECKIN, REGISTER"
                maxLength={20}
              />
              <div className="text-xs text-gray-500">
                Customers can text this keyword to your SMS number to join this queue. Must be unique per phone number.
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="singlePhone"
                checked={formData.single_active_phone}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, single_active_phone: checked === true }))}
              />
              <Label htmlFor="singlePhone" className="text-sm font-normal">
                Single Active Phone (prevent duplicate entries from same phone)
              </Label>
            </div>

            <div className="space-y-3 border-t pt-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="partySizeEnabled"
                  checked={formData.party_size_enabled}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, party_size_enabled: checked === true }))}
                />
                <Label htmlFor="partySizeEnabled" className="text-sm font-normal">
                  Enable Party Size (allow customers to specify group size)
                </Label>
              </div>
              
              {formData.party_size_enabled && (
                <div className="ml-6 space-y-3">
                  <div className="space-y-2">
                    <Label htmlFor="maxPartySize">Maximum Party Size</Label>
                    <Input
                      id="maxPartySize"
                      type="number"
                      min="1"
                      max="50"
                      value={formData.max_party_size}
                      onChange={(e) => setFormData(prev => ({ ...prev, max_party_size: parseInt(e.target.value) || 10 }))}
                      className="w-32"
                    />
                    <div className="text-xs text-gray-500">Maximum number of people allowed in a single party</div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="changePartySizeMode">Allow Party Size Changes</Label>
                    <Select
                      value={formData.change_party_size_mode}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, change_party_size_mode: value }))}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="disabled">Disabled</SelectItem>
                        <SelectItem value="increase_only">Increase only</SelectItem>
                        <SelectItem value="decrease_only">Decrease only</SelectItem>
                        <SelectItem value="both">Increase and decrease</SelectItem>
                      </SelectContent>
                    </Select>
                    <div className="text-xs text-gray-500">
                      Control whether customers can change their party size via SMS after joining
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <Label>Questions</Label>
                <Button type="button" variant="outline" size="sm" onClick={addQuestion}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add Question
                </Button>
              </div>
              
              {formData.questions.map((question, index) => (
                <div key={index} className="p-3 border rounded space-y-2">
                  <div className="grid grid-cols-12 gap-2 items-end">
                    <div className="col-span-6">
                      <Label className="text-xs">Question Text</Label>
                      <Input
                        value={question.label}
                        onChange={(e) => updateQuestion(index, 'label', e.target.value)}
                        placeholder="Enter your question here"
                        className="text-sm"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs">Type</Label>
                      <Select
                        value={question.type}
                        onValueChange={(value) => updateQuestion(index, 'type', value)}
                      >
                        <SelectTrigger className="text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="text">Text</SelectItem>
                          <SelectItem value="number">Number</SelectItem>
                          <SelectItem value="select">Select</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-3 flex items-center">
                      <Checkbox
                        checked={question.required}
                        onCheckedChange={(checked) => updateQuestion(index, 'required', checked === true)}
                      />
                      <Label className="text-xs ml-1">Required</Label>
                    </div>
                    <div className="col-span-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeQuestion(index)}
                        className="text-red-600"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  
                  {question.type === 'select' && (
                    <div>
                      <Label className="text-xs">Options (one per line)</Label>
                      <Textarea
                        value={(question.options || []).join('\n')}
                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => updateQuestion(index, 'options', e.target.value.split('\n'))}
                        placeholder="Morning\nAfternoon\nEvening"
                        className="text-sm min-h-[60px] resize-none"
                        rows={3}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>

            {error && (
              <div className="text-sm text-red-600 bg-red-50 p-2 rounded">
                {error}
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => {
                setShowCreateModal(false)
                setShowConfigModal(false)
              }}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? 'Saving...' : 'Save Queue'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* QR Code Modal */}
      <Dialog open={showQrModal} onOpenChange={setShowQrModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Queue QR Code</DialogTitle>
            <DialogDescription>
              Scan this QR code or use the URL to join the queue
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Queue URL</Label>
              <div className="flex gap-2">
                <Input value={qrUrl} readOnly className="flex-1" />
                <Button onClick={copyToClipboard} variant="outline">
                  Copy
                </Button>
              </div>
            </div>
            
            <div className="flex justify-center p-4 bg-white border rounded">
              <div id="qr-code-container" className="w-48 h-48 flex items-center justify-center">
                {qrUrl && (
                  <img 
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(qrUrl)}`}
                    alt="QR Code"
                    className="w-full h-full"
                  />
                )}
              </div>
            </div>
          </div>
          
          <DialogFooter className="flex gap-2">
            <Button onClick={downloadQRCode} variant="outline">
              <Download className="h-4 w-4 mr-2" />
              Download PNG
            </Button>
            <Button onClick={() => setShowQrModal(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )

  return (
    <TenantGuard>
      {queuesContent}
    </TenantGuard>
  )
}